import React, { useMemo } from "react";
import ReactMarkdown from "react-markdown";
import { useSelector } from "react-redux";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { vscDarkPlus } from "react-syntax-highlighter/dist/esm/styles/prism";
import { FiCheckCircle, FiTarget } from "react-icons/fi";
import { FaCoins } from "react-icons/fa";

function Badge({ children, className = "" }) {
  return (
    <span
      className={
        "inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs tracking-wide " +
        className
      }
    >
      {children}
    </span>
  );
}

const StatusIcon = ({ status }) => {
  switch (status) {
    case "Accepted":
      return (
        <span className="inline-flex items-center gap-1 text-emerald-300">
          <FiCheckCircle className="shrink-0" />
          <span className="text-xs">Completed</span>
        </span>
      );
    case "Attempted":
    case "Reset":
      return (
        <span className="inline-flex items-center gap-1 text-amber-300">
          <FiTarget className="shrink-0" />
          <span className="text-xs">
            {status === "Reset" ? "Attempted & Reset" : "Attempted"}
          </span>
        </span>
      );
    default:
      return null;
  }
};

function CopyButton({ code }) {
  const onCopy = () => {
    try {
      navigator.clipboard.writeText(code);
    } catch {}
  };
  return (
    <button
      onClick={onCopy}
      className="rounded-md border border-white/10 bg-white/5 px-2 py-1 text-[11px] uppercase tracking-wider hover:bg-white/10"
      aria-label="Copy code"
      type="button"
    >
      Copy
    </button>
  );
}

function CompetitionMarkdown({ challenge }) {
  const { executionStatus, challengeProgress } = useSelector(
    (state) => state.competitions
  );

  const ch = challenge?.[0];
  const status =
    executionStatus?.submissionResults?.status || challengeProgress?.status;

  const levelPill = useMemo(() => {
    if (!ch?.level) return null;
    return (
      <Badge className="border-[var(--blood-orange)]/50 bg-[color:var(--blood-orange)]/10 text-[color:var(--blood-orange)]">
        {ch.level}
      </Badge>
    );
  }, [ch?.level]);

  return (
    <div
      className="bg-zinc-950 text-slate-100 h-full overflow-y-auto"
      id="challenge-panel"
    >
      {/* Local spooky styles */}
      <style jsx="true">{`
        @import url("https://fonts.googleapis.com/css2?family=Creepster&family=Nosifer&family=Griffy&family=Fira+Code:wght@400;600&display=swap");

        :root {
          --blood-orange: #ff4500;
          --midnight-purple: #2d1b69;
          --toxic-green: #39ff14;
          --bone-white: #f8f8e8;
          --moonlight: #c0c5ce;
        }

        .spooky-header {
          background: radial-gradient(
              120% 200% at 20% -40%,
              rgba(255, 69, 0, 0.15),
              transparent 60%
            ),
            radial-gradient(
              140% 220% at 120% 0%,
              rgba(57, 255, 20, 0.12),
              transparent 70%
            ),
            var(--midnight-purple);
        }

        .spooky-title {
          font-family: "Nosifer", cursive;
          letter-spacing: 2px;
          text-shadow: 0 0 20px rgba(255, 69, 0, 0.35), 2px 2px 0 #0a0a0a;
        }

        .spooky-sub {
          font-family: "Creepster", cursive;
          letter-spacing: 1px;
        }

        /* Drip divider under the header (opaque) */
        .drip {
          position: relative;
          height: 10px;
          background: linear-gradient(180deg, var(--midnight-purple), #0a0a0a);
        }
        .drip::after {
          content: "";
          position: absolute;
          inset: 0;
          background: radial-gradient(
              12px 10px at 5% 60%,
              #0a0a0a 99%,
              transparent 100%
            ),
            radial-gradient(10px 12px at 20% 20%, #0a0a0a 99%, transparent 100%),
            radial-gradient(14px 9px at 40% 70%, #0a0a0a 99%, transparent 100%),
            radial-gradient(10px 14px at 60% 30%, #0a0a0a 99%, transparent 100%),
            radial-gradient(12px 10px at 80% 65%, #0a0a0a 99%, transparent 100%),
            radial-gradient(11px 9px at 95% 40%, #0a0a0a 99%, transparent 100%);
          mix-blend-mode: normal;
        }

        /* Markdown theming */
        .md p {
          color: #d9e0ea;
        }
        .md h2,
        .md h3,
        .md h4 {
          font-family: "Griffy", serif;
          color: var(--bone-white);
        }
        .md a {
          color: var(--toxic-green);
          text-decoration: underline;
          text-underline-offset: 4px;
        }
        .md blockquote {
          border-left: 4px solid var(--toxic-green);
          background: rgba(57, 255, 20, 0.06);
          color: #d6ffd9;
        }
        .md table {
          background: rgba(45, 27, 105, 0.35);
        }
        .md th {
          color: #c4b5fd;
        }
        .md code:not([class*="language-"]) {
          font-family: "Fira Code", monospace;
          border: 1px solid rgba(255, 255, 255, 0.12);
          background: rgba(10, 10, 10, 0.6);
        }
      `}</style>

      {/* OPAQUE SPOOKY HEADER */}
      <div className="sticky top-0 z-10 border-b border-white/10">
        <div className="spooky-header relative">
          {/* subtle web in the corner */}
          <svg
            className="absolute right-4 top-4 w-24 h-24 opacity-40"
            viewBox="0 0 300 300"
            aria-hidden="true"
          >
            <g stroke="#C0C5CE" strokeWidth="1" fill="none">
              <path d="M150,0 L150,300 M0,150 L300,150 M0,0 L300,300 M300,0 L0,300" />
              <circle cx="150" cy="150" r="50" />
              <circle cx="150" cy="150" r="100" />
              <circle cx="150" cy="150" r="150" />
            </g>
          </svg>

          <div className="px-8 pt-8 pb-5">
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div className="min-w-0">
                <h1 className="spooky-title text-2xl md:text-3xl font-black leading-tight text-[color:var(--bone-white)]">
                  {ch?.name || "Challenge"}
                </h1>

                <div className="mt-2 flex flex-wrap items-center gap-2 text-sm spooky-sub">
                  <StatusIcon status={status} />
                  {typeof ch?.points === "number" && (
                    <Badge className="border-[var(--toxic-green)]/40 bg-[color:var(--toxic-green)]/10 text-[color:var(--toxic-green)]">
                      <FaCoins className="text-yellow-300" />
                      <span className="text-yellow-200">{ch.points} pts</span>
                    </Badge>
                  )}
                  {levelPill}
                  {ch?.tag && (
                    <Badge className="border-[var(--blood-orange)]/50 bg-[color:var(--blood-orange)]/10 text-[color:var(--blood-orange)]">
                      #{ch.tag}
                    </Badge>
                  )}
                </div>
              </div>

              {/* compact KPIs (kept opaque) */}
              {/* <div className="flex items-center gap-3 text-xs">
                {status && (
                  <span className="rounded-md border border-white/10 bg-white/5 px-3 py-1">
                    Status:{" "}
                    <strong className="text-emerald-300">{status}</strong>
                  </span>
                )}
                {typeof ch?.points === "number" && (
                  <span className="rounded-md border border-white/10 bg-white/5 px-3 py-1">
                    Points:{" "}
                    <strong className="text-yellow-300">{ch.points}</strong>
                  </span>
                )}
              </div> */}
            </div>
          </div>
        </div>
        <div className="drip" />
      </div>

      {/* MARKDOWN CONTENT */}
      <div className="px-8 py-8 md:py-10 md:px-10 md:pr-8 md:pb-12 md:max-w-[1200px] md:mx-auto md:pl-10">
        <div className="md">
          <ReactMarkdown
            children={ch?.challenge}
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeRaw]}
            components={{
              h1: ({ node, ...props }) => (
                <h2
                  className="mt-8 mb-3 text-2xl font-extrabold tracking-tight"
                  {...props}
                />
              ),
              h2: ({ node, ...props }) => (
                <h3
                  className="mt-7 mb-3 text-xl font-bold tracking-tight"
                  {...props}
                />
              ),
              h3: ({ node, ...props }) => (
                <h4
                  className="mt-6 mb-2 text-lg font-semibold tracking-tight"
                  {...props}
                />
              ),
              p: ({ node, ...props }) => (
                <p className="mb-4 leading-7" {...props} />
              ),
              ul: ({ node, ...props }) => (
                <ul className="mb-4 list-disc pl-6 space-y-1" {...props} />
              ),
              ol: ({ node, ...props }) => (
                <ol className="mb-4 list-decimal pl-6 space-y-1" {...props} />
              ),
              li: ({ node, ...props }) => (
                <li className="leading-7" {...props} />
              ),
              a: ({ node, ...props }) => (
                <a
                  className="underline decoration-emerald-600/50 underline-offset-4 hover:text-emerald-200"
                  target="_blank"
                  rel="noreferrer"
                  {...props}
                />
              ),
              blockquote: ({ node, ...props }) => (
                <blockquote
                  className="my-5 px-5 py-3 italic rounded-md"
                  {...props}
                />
              ),
              hr: () => (
                <div className="my-8 h-px w-full bg-gradient-to-r from-transparent via-white/10 to-transparent" />
              ),
              table: ({ node, ...props }) => (
                <div className="my-6 overflow-x-auto rounded-lg border border-white/10">
                  <table className="w-full border-collapse" {...props} />
                </div>
              ),
              thead: ({ node, ...props }) => (
                <thead className="bg-zinc-800/70" {...props} />
              ),
              th: ({ node, ...props }) => (
                <th
                  className="border-b border-white/10 px-3 py-2 text-left"
                  {...props}
                />
              ),
              td: ({ node, ...props }) => (
                <td className="px-3 py-2 align-top" {...props} />
              ),
              code({ node, inline, className, children, ...props }) {
                const match = /language-(\w+)/.exec(className || "");
                const codeText = String(children).replace(/\n$/, "");
                if (!inline && match) {
                  return (
                    <div className="my-5 overflow-hidden rounded-lg border border-white/10 bg-zinc-900">
                      <div className="flex items-center justify-between border-b border-white/10 px-3 py-2 text-[11px] uppercase tracking-wider text-slate-300/80">
                        <span className="spooky-sub">{match[1]}</span>
                        <CopyButton code={codeText} />
                      </div>
                      <SyntaxHighlighter
                        children={codeText}
                        style={vscDarkPlus}
                        language={match[1]}
                        PreTag="div"
                        customStyle={{ margin: 0, background: "transparent" }}
                        {...props}
                      />
                    </div>
                  );
                }
                return (
                  <code
                    className={
                      "rounded-md px-1.5 py-0.5 text-[13px] " +
                      (className || "")
                    }
                    {...props}
                  >
                    {children}
                  </code>
                );
              },
            }}
          />
        </div>
      </div>
    </div>
  );
}

export default CompetitionMarkdown;
